import { AGREEMENT_SIGNER_ROLE, SigningMode, SigningOrderParty } from './signing-order.types';

export const DEFAULT_SIGNING_MODE: SigningMode = 'AgtSignSequential';

const toPositiveSequence = (value: any, fallback: number): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : fallback;
};

const inferPartyTypeEnumId = (party: SigningOrderParty, selectedParty: any) => {
  if (party.partyTypeEnumId || selectedParty.partyTypeEnumId) return party.partyTypeEnumId || selectedParty.partyTypeEnumId;
  const partyTypeEnum = String(selectedParty.partyTypeEnum || '').toLowerCase();
  if (partyTypeEnum.includes('tổ chức') || partyTypeEnum.includes('to chuc') || party.taxCode || selectedParty.taxCode || selectedParty.partyTaxId) {
    return 'PtyOrganization';
  }
  return 'PtyPerson';
};

export const normalizeSigningParties = <T extends SigningOrderParty>(parties: T[] = []): T[] => {
  const rawSequences = Array.from(
    new Set(parties.map((party, index) => toPositiveSequence(party.sequenceNum ?? party.signingOrder, index + 1)))
  ).sort((a, b) => a - b);

  const sequenceMap = new Map<number, number>();
  rawSequences.forEach((sequenceNum, index) => sequenceMap.set(sequenceNum, index + 1));

  return parties.map((party, index) => {
    const rawSequence = toPositiveSequence(party.sequenceNum ?? party.signingOrder, index + 1);
    const sequenceNum = sequenceMap.get(rawSequence) || index + 1;
    const selectedParty = party.party || {};
    const partyId = party.partyId || selectedParty.partyId || selectedParty.value || '';
    const partyName = party.partyName || selectedParty.partyName || selectedParty.label || '';
    const representativeName = party.representativeName || selectedParty.representativeName || '';
    const partyTypeEnumId = inferPartyTypeEnumId(party, selectedParty);
    const recipientInfoStatus = party.recipientInfoStatus || selectedParty.recipientInfoStatus || 'KNOWN';

    return {
      ...party,
      _internalId: party._internalId || `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
      partyId,
      partyName,
      representativeName,
      emailAddress: party.emailAddress || selectedParty.emailAddress || selectedParty.email || '',
      partyTypeEnumId,
      recipientInfoStatus,
      roleTypeId: AGREEMENT_SIGNER_ROLE,
      sequenceNum,
      signingOrder: sequenceNum
    };
  });
};

export const validateSigningParties = (parties: SigningOrderParty[] = []): string[] => {
  const errors: string[] = [];
  const partyIds = new Set<string>();

  if (!parties.length) {
    errors.push('Vui lòng thêm ít nhất một người ký');
    return errors;
  }

  normalizeSigningParties(parties).forEach((party, index) => {
    const rowName = party.partyName || `Người ký ${index + 1}`;
    const partyId = party.partyId || party.party?.partyId || party.party?.value;
    const isUnknownRecipient = party.recipientInfoStatus === 'UNKNOWN';

    if (isUnknownRecipient) {
      if (!party.partyName || !party.partyName.trim()) {
        errors.push(`Người ký ${index + 1}: chưa nhập tên vị trí`);
      }
      return;
    }

    if (!partyId || partyId === '_NA_' || String(partyId).startsWith('ROLE_') || String(partyId).startsWith('TPL_SLOT_')) {
      errors.push(`${rowName}: chưa chọn đối tượng`);
    } else if (partyIds.has(String(partyId))) {
      errors.push(`${rowName}: người ký bị trùng trong tài liệu`);
    } else {
      partyIds.add(String(partyId));
    }

    if (!party.emailAddress || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(party.emailAddress)) {
      errors.push(`${rowName}: email không hợp lệ`);
    }
  });

  return errors;
};
